<template>
    <flex-element
        ref="carousel"
        :element-type="elementType"
        class="f-relative f-flex"
        class-default-inline="f-justify-start mob:f-flex-col f-flex-row f-min-w-30 mob:f-min-w-1/1 f-min-h-32 f-w-1/1 f-overflow-hidden"
    >
        <!-- ---------- carousel editor controls: ---------- -->
        <collection-controls
            :visible-index="currentSlideIndex"
            :num-elements="elementsNew.length"
            @add="onAdd"
            @delete="onDelete"
            @previous="onPrevious"
            @next="onNext"
            @select="onSelect"
        />

        <transition-group
            tag="div"
            name="transition-slide"
            :enter-active-class="slideAnimationClasses.enter"
            :leave-active-class="slideAnimationClasses.leave"
            class="f-overflow-x-hidden f-w-1/1 f-h-1/1"
        >
            <!-- ---------- slides (editor + public): ---------- -->
            <div
                v-for="(rendering, index) in elementsNew"
                v-show="showSlide(index)"
                :key="rendering.id"
                class="fu-placement-fill"
            >
                <component
                    :is="rendering.elementType"
                    class="f-w-1/1"
                    v-bind="rendering"
                    :index="index"
                    :pointer="index + 1"
                    :limit="1"
                    top-level
                    @element-updated="elementUpdated"
                />
            </div>
        </transition-group>

        <!-- ---------- carousel controls (public): ---------- -->
        <!-- arrows: -->
        <button
            v-if="controlsAreVisible"
            type="button"
            class="fu-placement-left f-p-2.5 f-text-3 f-opacity-50 hover:f-opacity-100 f-text-gray-400 f-z-20 f-cursor-pointer f-flex f-items-center"
            aria-label="previous slide"
            @click="onArrowBackClick"
        >
            <a-icon icon="chevron-left" />
        </button>

        <button
            v-if="controlsAreVisible"
            type="button"
            class="fu-placement-right f-p-2.5 f-text-3 f-opacity-50 hover:f-opacity-100 f-text-gray-400 f-z-20 f-cursor-pointer f-flex f-items-center"
            aria-label="next slide"
            @click="onArrowNextClick"
        >
            <a-icon icon="chevron-right" />
        </button>

        <!-- bullets: -->
        <div v-if="controlsAreVisible" class="f-flex f-flex-row f-justify-center f-z-20 fu-placement-bottom f-mb-2 mob:f-hidden bullets">
            <a-icon
                v-for="(n, index) in elementsNew"
                :key="index"
                v-group:carousel-dots
                :class="{ 'f-opacity-50': (index !== currentSlideIndex) }"
                class="f-mx-0.25 f-cursor-pointer"
                icon="circle"
                set="fas"
                aria-label="select slide"
                @click="onBulletClick(index)"
            />
        </div>
    </flex-element>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import uuidv4 from 'uuid/v4';

import baseOrganism from '@/mixins/base-organism/base-organism-smart';
import CollectionControls from '@/components/utilities/collection-controls';
import Timer from '@/utils/timer';
import { Organism } from '@/data/data-models';
import { CLEAR_SELECTED_ELEMENTS } from '@/store/types/mutation-types';

const DEFAULT_CAROUSEL_SPEED = '4'; // in seconds (String)

export default {
    components: { CollectionControls },
    mixins: [baseOrganism],

    props: {
        displayName: {
            type: String,
            default: 'Carousel',
        },
    },

    data() {
        return {
            slideDirection: 'next',
            timer: null,
            currentSlideIndex: -1,
        };
    },

    computed: {
        ...mapState({
            previewMode: state => state.previewMode,
            updatedCarouselSpeed: state => state.page.updatedCarouselSpeed,
        }),

        carouselSpeed() {
            let output;
            let speed = DEFAULT_CAROUSEL_SPEED;

            if (this.hasElementData('carousel-speed')) {
                speed = this.getElementData('carousel-speed');
            }

            if (speed.toLowerCase() === 'none') {
                output = 999999999;

            // convert seconds to milliseconds:
            } else {
                output = Number(speed) * 1000;
            }

            return output;
        },

        slideAnimationClasses() {
            let cssObject;

            switch (this.getElementData('carousel-transition-effect')) {
                case 'slide':
                    if (this.slideDirection === 'prev') {
                        cssObject = {
                            enter: 'slideInLeft animated fast',
                            leave: 'slideOutRight animated fast',
                        };
                    } else {
                        cssObject = {
                            enter: 'slideInRight animated fast',
                            leave: 'slideOutLeft animated fast',
                        };
                    }
                    break;

                case 'slide reverse':
                    if (this.slideDirection === 'prev') {
                        cssObject = {
                            enter: 'slideInRight animated fast',
                            leave: 'slideOutLeft animated fast',
                        };
                    } else {
                        cssObject = {
                            enter: 'slideInLeft animated fast',
                            leave: 'slideOutRight animated fast',
                        };
                    }
                    break;

                case 'fade':
                    cssObject = {
                        enter: 'fadeIn animated',
                        leave: 'fadeOut animated',
                    };
                    break;
                default:
                    break;
            }

            return cssObject;
        },

        controlsAreVisible() {
            return this.elementsNew.length > 1
                && !this.hasElementData('hide-carousel-controls');
        },
    },

    watch: {
        previewMode() {
            if (this.previewMode) {
                this.timer.reset(this.carouselSpeed);
            } else {
                this.timer.stop();
            }
        },

        updatedCarouselSpeed() {
            if (this.updatedCarouselSpeed.el === this) {
                this.setElementData('carousel-speed', this.updatedCarouselSpeed.speed);
            }
        },
    },

    created() {
        this.atomicType = 'organism';
        this.templateType = 'flex-carousel';

        // if transition effect is not set, set default:
        if (!this.getElementData('carousel-transition-effect')) {
            this.setElementData('carousel-transition-effect', 'slide', { triggerChangeDetection: false });
        }
    },

    mounted() {
        // automatically change slides on a regular interval:
        this.timer = new Timer(this.onArrowNextClick, this.carouselSpeed);

        if (!this.elementsNew.length) {
            this.onAdd(true);
            return;
        }

        if (this.previewMode) {
            this.timer.start();
        }

        this.currentSlideIndex = 0;
    },

    methods: {
        ...mapMutations({
            clearSelectedElements: CLEAR_SELECTED_ELEMENTS,
        }),

        onSelect() {
            this.setSelectedElement({ element: this });
            this.setSelectedRendering({ rendering: this });
        },

        showSlide(index) {
            return index === this.currentSlideIndex;
        },

        onAdd(isDefaultAdd) {
            const pointer = this.elementsNew.length + 1;
            let element = null;

            element = new Organism({
                id: uuidv4(),
                name: `drop-zone-single_${ pointer }`,
                elementType: 'drop-zone-single',
                pointer,
                classes: 'f-h-1/1',
                templateType: 'Organism',
                dataSourceId: '00000000-0000-0000-0000-000000000000',
                elements: [],
            });

            this.elementsNew.push(element);
            this.currentSlideIndex = this.elementsNew.length - 1;

            if (!isDefaultAdd) this.setChangedData(true);
        },

        onDelete() {
            this.elementsNew.splice(this.currentSlideIndex, 1);
            this.clearSelectedElements();
            this.setChangedData(true);

            if (this.currentSlideIndex >= this.elementsNew.length) {
                this.currentSlideIndex--;
            }
        },

        onPrevious() {
            const lastIndex = this.elements.length - 1;

            this.slideDirection = 'prev';

            this.currentSlideIndex = this.currentSlideIndex <= 0
                ? lastIndex
                : this.currentSlideIndex - 1;
        },

        onNext() {
            const lastIndex = this.elements.length - 1;

            this.slideDirection = 'next';

            this.currentSlideIndex = this.currentSlideIndex >= lastIndex
                ? 0
                : this.currentSlideIndex + 1;
        },

        onArrowBackClick() {
            this.onPrevious();

            if (this.previewMode) {
                this.timer.reset(this.carouselSpeed);
            }
        },

        onArrowNextClick() {
            this.onNext();

            if (this.previewMode) {
                this.timer.reset(this.carouselSpeed);
            }
        },

        onBulletClick(clickedIndex) {
            // early return:
            if (clickedIndex === this.currentSlideIndex) {
                return;
            }

            if (clickedIndex > this.currentSlideIndex) {
                this.slideDirection = 'next';
            } else if (clickedIndex < this.currentSlideIndex) {
                this.slideDirection = 'prev';
            }
            this.currentSlideIndex = clickedIndex;

            if (this.previewMode) {
                this.timer.reset(this.carouselSpeed);
            }
        },
    },
};
</script>
